import React, { useState } from 'react';
import { Row, Col } from 'reactstrap';
import { Field, FieldArray } from 'formik';
import { useTenant } from '../../../../hooks/useTenant';
import { storage } from '../../../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { ACHIEVEMENT_ICONS, getAttendanceImage } from '../../../../models/gamification';
import { NeoInput, NeoButton } from '../../../../components/Common';

const GamificationAttendanceMilestones = ({ values, setFieldValue }) => {
    const [editingIndex, setEditingIndex] = useState(null);
    const [isUploading, setIsUploading] = useState({});
    const { idTenant } = useTenant();

    const handleImageUpload = async (e, globalIndex, achivId) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Por favor, selecione uma imagem válida.');
            return;
        }

        if (file.size > 2 * 1024 * 1024) { // 2MB limit
            toast.error('A imagem deve ter no máximo 2MB.');
            return;
        }

        setIsUploading(prev => ({ ...prev, [globalIndex]: true }));

        try {
            const fileExtension = file.name.split('.').pop();
            const fileName = `${achivId}_${Date.now()}.${fileExtension}`;
            const storageRef = ref(storage, `tenants/${idTenant}/gamification/achievements/${fileName}`);

            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);

            setFieldValue(`achievements.${globalIndex}.imageId`, downloadURL);
            toast.success('Imagem atualizada com sucesso!');
        } catch (error) {
            console.error("Erro ao fazer upload:", error);
            toast.error('Erro ao fazer upload da imagem.');
        } finally {
            setIsUploading(prev => ({ ...prev, [globalIndex]: false }));
        }
    };

    return (
        <div className="mt-5 mb-4 border-top pt-4">
            <div className="mb-4 d-flex align-items-center">
                <div className="neo-section-icon-circle flex-shrink-0">
                    <i className="mdi mdi-trophy-outline font-size-18 text-accent" />
                </div>
                <div className="ms-3">
                    <h5 className="mb-0 fw-bold text-dark neo-font-outfit font-size-15">Métricas de Conquista por Frequência</h5>
                    <p className="text-muted mb-0 font-size-12">Medalhas e recompensas recebidas pelos alunos ao atingirem marcos de presença acumulados.</p>
                </div>
            </div>

            <FieldArray name="achievements">
                {({ push, remove }) => {
                    const attendanceIndices = values.achievements
                        .map((achiv, index) => ({ achiv, index }))
                        .filter(item => item.achiv.type === 'attendance');

                    // Sort by threshold visually
                    attendanceIndices.sort((a, b) => a.achiv.threshold - b.achiv.threshold);

                    return (
                        <>
                            <Row className="g-4">
                                {attendanceIndices.map(({ achiv, index }) => (
                                    <Col lg={3} md={4} sm={6} key={`achiv_${index}`}>
                                        {editingIndex === index ? (
                                            <div className="neo-gamification-card neo-gamification-card--editing text-start h-100">
                                                <button
                                                    type="button"
                                                    className="neo-btn-circle-delete"
                                                    onClick={() => {
                                                        remove(index);
                                                        setEditingIndex(null);
                                                    }}
                                                >
                                                    <i className="mdi mdi-trash-can-outline font-size-14"></i>
                                                </button>

                                                <Field name={`achievements.${index}.name`}>
                                                    {({ field, meta }) => (
                                                        <NeoInput
                                                            {...field}
                                                            label="Nome da Conquista *"
                                                            placeholder="Ex: Guerreiro"
                                                            error={meta.touched && meta.error ? meta.error : ''}
                                                            className="mb-2"
                                                            size="sm"
                                                        />
                                                    )}
                                                </Field>

                                                <Field name={`achievements.${index}.threshold`}>
                                                    {({ field, meta }) => (
                                                        <NeoInput
                                                            {...field}
                                                            type="number"
                                                            label="Meta (Aulas) *"
                                                            placeholder="Ex: 50"
                                                            error={meta.touched && meta.error ? meta.error : ''}
                                                            className="mb-2"
                                                            size="sm"
                                                        />
                                                    )}
                                                </Field>

                                                <Field name={`achievements.${index}.rewardXp`}>
                                                    {({ field, meta }) => (
                                                        <NeoInput
                                                            {...field}
                                                            type="number"
                                                            label="XP Recompensa *"
                                                            placeholder="Ex: 500"
                                                            error={meta.touched && meta.error ? meta.error : ''}
                                                            className="mb-2"
                                                            size="sm"
                                                        />
                                                    )}
                                                </Field>

                                                <div className="mb-2">
                                                    <label className="form-label font-size-11 text-muted text-uppercase fw-bold mb-1">
                                                        Upload de Imagem
                                                    </label>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="form-control form-control-sm bg-light border-0 py-1"
                                                        disabled={isUploading[index]}
                                                        onChange={(e) => handleImageUpload(e, index, achiv.id || `achiv_attendance_${index}`)}
                                                    />
                                                    {isUploading[index] && (
                                                        <small className="text-accent d-block mt-1">
                                                            <i className="mdi mdi-loading mdi-spin me-1"></i>Enviando...
                                                        </small>
                                                    )}
                                                </div>

                                                <Field name={`achievements.${index}.icon`}>
                                                    {({ field, meta }) => (
                                                        <NeoInput
                                                            {...field}
                                                            type="select"
                                                            label="Ícone Alternativo"
                                                            error={meta.touched && meta.error ? meta.error : ''}
                                                            className="mb-3"
                                                            size="sm"
                                                            options={ACHIEVEMENT_ICONS.map(icon => ({
                                                                value: icon,
                                                                label: icon.replace('mdi-', '')
                                                            }))}
                                                        />
                                                    )}
                                                </Field>

                                                <NeoButton 
                                                    variant="primary" 
                                                    size="sm" 
                                                    className="w-100 fw-bold"
                                                    onClick={() => setEditingIndex(null)}
                                                >
                                                    OK
                                                </NeoButton>
                                            </div>
                                        ) : (
                                            <div className="neo-gamification-card text-center h-100">
                                                <button
                                                    type="button"
                                                    className="neo-btn-circle-edit"
                                                    onClick={() => setEditingIndex(index)}
                                                >
                                                    <i className="mdi mdi-pencil font-size-14"></i>
                                                </button>

                                                <div className="neo-badge-floating">
                                                    {achiv.threshold} Aulas
                                                </div>

                                                <div className="mt-3 mb-2">
                                                    <div className="badge-image-container">
                                                        {achiv.imageId ? (
                                                            <img 
                                                                src={getAttendanceImage(achiv.imageId)} 
                                                                alt={achiv.name} 
                                                            />
                                                        ) : (
                                                            <i className={`mdi ${achiv.icon} text-warning font-size-48`}></i>
                                                        )}
                                                    </div>
                                                </div>
                                                <h6 className="fw-bold text-dark mb-1 font-size-13 neo-font-outfit">{achiv.name}</h6>
                                                
                                                <div className="d-flex align-items-center justify-content-center mt-3">
                                                    <span className="neo-badge-xp">
                                                        <i className="mdi mdi-star-circle me-1 font-size-13"></i>
                                                        +{achiv.rewardXp} XP
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </Col>
                                ))}

                                {attendanceIndices.length === 0 && (
                                    <Col md={12}>
                                        <div className="text-center p-5 bg-light-subtle d-flex flex-column align-items-center justify-content-center border border-2 border-dashed rounded-3" style={{ minHeight: "180px" }}>
                                            <i className="mdi mdi-trophy-broken font-size-36 text-muted mb-2 opacity-50"></i>
                                            <p className="text-muted small mb-0 fw-semibold">Nenhuma métrica de frequência configurada no momento.</p>
                                        </div>
                                    </Col>
                                )}
                            </Row>

                            <div className="mt-4 text-center">
                                <NeoButton
                                    type="button"
                                    variant="outline-primary"
                                    className="px-5 fw-bold shadow-sm"
                                    icon="mdi-plus-circle-outline"
                                    onClick={() => {
                                        push({
                                            id: `achiv_attendance_${Date.now()}`,
                                            name: 'Nova Conquista de Frequência',
                                            description: 'Recompensa por aulas assistidas',
                                            type: 'attendance',
                                            threshold: 10,
                                            icon: 'mdi-trophy',
                                            rewardXp: 10
                                        });
                                        setEditingIndex(values.achievements.length);
                                    }}
                                >
                                    Adicionar Nova Métrica
                                </NeoButton>
                            </div>
                        </>
                    );
                }}
            </FieldArray>
        </div>
    );
};

export default GamificationAttendanceMilestones;
